<template>
  <div class="edt">
    <v-app-bar
      flat
      color="transparent"
      class="main-appbar"
    >
      <app-bar-nav-icon />
      <div class="d-flex align-center">
        <v-toolbar-title>
          <h1 class="headline font-weight-bold">{{ pageTitle }}
          </h1>
        </v-toolbar-title>
      </div>
      <v-spacer />
      <div>
        <v-btn
          color="primary"
          class="mr-4"
          medium
          @click="onCancel"
          :disabled="form.$busy"
          text
          style="text-transform: none;"
        >Cancel</v-btn>
        <v-btn
          color="primary"
          medium
          @click="onSave"
          :loading="form.$busy"
          style="text-transform: none;"
        >Save</v-btn>
      </div>
    </v-app-bar>
    <v-row class="px-4 py-4">
      <v-col
        lg="8"
        md="6"
        sm="12"
        xs="12"
      >
        <v-card
          outlined
          class="px-6 py-8"
        >
          <v-row>
            <v-col>
              <v-checkbox
                label="Multiple votes"
                v-model="form.can_vote_multiple"
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-text-field
                :disabled="!form.can_vote_multiple"
                v-model="form.max_check"
                outlined
                label="Max votes"
                type="number"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-checkbox
                label="Open to guests"
                v-model="form.can_visitors_vote"
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-checkbox
                label="Visible results"
                v-model="form.can_voter_see_result"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="12">
              <v-textarea
                outlined
                rows="4"
                name="input-7-4"
                label="Poll Question"
                v-model="form.question"
                :error-messages="form.$getError('question')"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="12"
            >
              <v-card
                outlined
                :class="{'options-error' : optionsHasError}"
              >
                <v-card-title :class="{'error--text' : optionsHasError}">Poll Options</v-card-title>
                <v-card-text>
                  <v-row
                    :key="i"
                    v-for="(option, i) in form.options"
                  >
                    <v-col
                      cols="12"
                      md="10"
                    >
                      <v-text-field
                        dense
                        flat
                        outlined
                        v-model="form.options[i].name"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="1"
                    >
                      <v-icon
                        @click="removeOption(i, form.options[i].id)"
                        class="remove-reason"
                        color="error lighten-1"
                      >mdi-delete</v-icon>
                    </v-col>
                  </v-row>

                </v-card-text>
                <v-card-actions>
                  <b
                    @click="addOption"
                    class="text-caption font-weight-bold add-btn"
                  >Add item</b>
                </v-card-actions>
              </v-card>
              <p
                class="error--text"
                v-show="optionsHasError"
              >{{ optionsErrorMessage }}</p>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col
        lg="4"
        md="6"
        sm="12"
      >
        <v-card
          v-show="isModeUpdate"
          outlined
          class="px-6 pt-6"
        >
          <div class="d-flex flex-row justify-space-between align-center pb-3 ">
            <label class="subtitle-1 d-block grey--text">Generated HTML</label>
          </div>
          <v-text-field
            outlined
            label="HTML snippet"
            readonly
            :value="pollHtml(form.id)"
          ></v-text-field>
          <div class="pb-4 d-flex flex-row-reverse">
            <v-btn
              x-small
              color="primary"
              text
              v-clipboard:copy="pollHtml(form.id)"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >Copy to clipboard</v-btn>

          </div>
        </v-card>

        <published-date-selector
          :class="{'mt-6' : isModeUpdate}"
          pageTitle="Starts at"
          v-model="form.starts_at"
          :errorMessages="head(form.$getError('starts_at'))"
        />

        <published-date-selector
          class="mt-6"
          pageTitle="Ends at"
          v-model="form.ends_at"
          :errorMessages="head(form.$getError('ends_at'))"
        />

        <published-date-selector
          class="mt-6"
          pageTitle="Closed at"
          v-model="form.is_closed_at"
          :errorMessages="head(form.$getError('is_closed_at'))"
        />
      </v-col>
    </v-row>
    <v-overlay
      absolute
      :value="updating"
    >
      <v-dialog
        v-model="updating"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            {{ loading_message }}
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-overlay>
    <v-snackbar
      v-model="snackbar.show"
      right
      :color="snackbar.color"
    >
      {{ snackbar.message }}
      <v-btn
        text
        @click="snackbar.show = false"
      >OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Form from '@/utils/form'
import get from 'lodash/get'
import head from 'lodash/head'
import find from 'lodash/find'
import map from 'lodash/map'
import replace from 'lodash/replace'
import has from 'lodash/has'
import isNull from 'lodash/isNull'
import SBPoll from '@/models/SBPoll'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import { mapState, mapActions } from 'vuex'
import PublishedDateSelector from '@/views/Home/Articles/shared/PublishedDateSelector'
import dates from '@/utils/dates'
import isArray from 'lodash/isArray'

const initialData = {
  question: null,
  can_vote_multiple: false,
  max_check: 1,
  can_visitors_vote: false,
  can_voter_see_result: false,
  is_closed_at: null,
  starts_at: null,
  ends_at: null,
  options: [],
  deleted_options: []

}

export default {
  data () {
    return {
      loading: false,
      updating: false,
      loading_message: 'Loading',
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      },
      loadingTags: false,
      form: new Form({
        ...initialData
      })
    }
  },
  props: {
    sBPoll: {
      type: [SBPoll, Object],
      default () {
        return {}
      }
    },
    pageTitle: {
      type: String,
      default: 'SportsBetting Poll'
    },
    redirectCancelTo: {
      type: [String, Object],
      default () {
        return {
          name: 'sportsbetting-polls'
        }
      }
    },
    redirectSaveTo: {
      type: [String, Object],
      default () {
        return {
          name: 'sportsbetting-polls'
        }
      }
    }
  },

  components: {
    AppBarNavIcon,
    PublishedDateSelector
  },

  methods: {
    head,
    replace,
    ...mapActions({
      updateRecord: 'SBPoll/update'
    }),

    onCancel () {
      this.$router.replace(this.redirectCancelTo)
    },

    async onSave () {
      try {
        this.form.$busy = true
        this.form.$clearErrors()
        const data = this.form.$data()

        if (has(data, 'is_closed_at') && !isNull(data.is_closed_at)) {
          data.is_closed_at = dates.localDateToUTC(data.is_closed_at).format('YYYY-MM-DD HH:mm:ss')
        }

        if (has(data, 'starts_at') && !isNull(data.starts_at)) {
          data.starts_at = dates.localDateToUTC(data.starts_at).format('YYYY-MM-DD HH:mm:ss')
        }

        if (has(data, 'ends_at') && !isNull(data.ends_at)) {
          data.ends_at = dates.localDateToUTC(data.ends_at).format('YYYY-MM-DD HH:mm:ss')
        }

        data.options = data.options.filter(function (option) {
          if (!isNull(option.name)) return option
        })

        const sBPoll = new SBPoll(data)
        await sBPoll.save()

        this.$router.replace(this.redirectSaveTo)
      } catch (error) {
        if (get(error, 'response.status') === 422) {
          if (get(error, 'response.data.errors') !== null) {
            this.form.$setErrors(get(error, 'response.data.errors'))
          } else {
            this.showSnackbar('Something went wrong.', 'error')
          }
        } else {
          this.showSnackbar(error, 'error')
        }
      } finally {
        this.form.$busy = false
      }
    },
    showSnackbar (message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    addOption () {
      this.form.options.push({
        name: null
      })
    },
    removeOption (i, id = null) {
      this.form.options.splice(i, 1)

      if (this.isModeUpdate) {
        this.form.deleted_options.push(id)
      }
    },
    onCopy: function (e) {
      this.showSnackbar('HTML snippet copied.', 'success')
    },
    onError: function (e) {
      this.showSnackbar('Failed to copy.', 'error')
    },
    pollHtml (id) {
      return '&zwj;<div data-widget="sb101-poll" data-widget-append="{&quot;pollId&quot;: &quot;' + id + '&quot;}"> </div>&zwj;'
    }

  },

  watch: {
    sBPoll: {
      immediate: true,
      handler (val) {
        if (val) {
          this.form = new Form({
            ...initialData,
            ...val,
            options: val.poll_options ? val.poll_options : []
          })
        }
      }
    },
    'form.can_vote_multiple': function (newVal, oldVal) {
      if (newVal === false) {
        this.form.max_check = 1
      }
    }
  },

  computed: {
    isModeUpdate () {
      return get(this.form, 'id', null) !== null
    },
    optionsHasError () {
      return this.optionsErrorMessage !== null
    },
    optionsErrorMessage () {
      if (this.form.$getError('options')) {
        if (isArray(this.form.$getError('options'))) {
          return head(this.form.$getError('options'))
        }
        return this.form.$getError('options')
      }
      return null
    }
  },

  created () {
    this.form.deleted_options = []
  }
}
</script>

<style scoped>
.add-btn {
  color: #df4661;
  cursor: pointer;
}
.options-error {
  border: 2px solid var(--v-error-base) !important;
}
</style>
